/**
 * consentGroups.ts — the headings a consent question's declarations sit under,
 * or `null` for the flat list.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * clinic-service question set v6 (2026-09-24, CTO — the categorised agreement
 * step) serves `optionGroups` on q15: "About you", "Your treatment", "Your
 * ongoing care". The headings are PRESENTATION ONLY. Every declaration is a
 * read-only list item (no box of its own since 2026-09-24 v2), still held and
 * submitted by its own key exactly as before; a heading is never something the
 * patient agrees to, and grouping never changes the submitted answer
 * (`options.map(o => o.key)`, in the server's option order).
 *
 * ⚠️ FALL BACK RATHER THAN DROP. The groups are used only when they account for
 * every option key EXACTLY ONCE. The one "I agree to all of the above" tick
 * agrees to EVERY key — including any the groups failed to show — so a group
 * list that misses a key would hide a declaration the patient then agrees to
 * unseen, the one failure this screen cannot have; and one that repeats a key
 * would show the same declaration twice. Either way, and for any other
 * malformed shape (an empty heading, an empty group, a key the question does
 * not have), the screen renders the flat list exactly as it did before v6.
 * clinic-service validates the same rule at seed time; this is the portal
 * refusing to trust that it always will.
 */
import type { QuestionOption, QuestionPublic } from '../api/types';

export interface ConsentGroup {
  heading: string;
  options: QuestionOption[];
}

export function consentGroups(question: QuestionPublic): ConsentGroup[] | null {
  const options = question.options ?? [];
  const groups: unknown = question.optionGroups;
  if (!Array.isArray(groups) || groups.length === 0 || options.length === 0) return null;

  const byKey = new Map(options.map((option) => [option.key, option]));
  const seen = new Set<string>();
  const result: ConsentGroup[] = [];

  for (const group of groups as unknown[]) {
    if (typeof group !== 'object' || group === null) return null;
    const { heading, optionKeys } = group as { heading?: unknown; optionKeys?: unknown };
    if (typeof heading !== 'string' || heading.trim().length === 0) return null;
    if (!Array.isArray(optionKeys) || optionKeys.length === 0) return null;
    const members: QuestionOption[] = [];
    for (const key of optionKeys) {
      if (typeof key !== 'string') return null;
      const option = byKey.get(key);
      // Unknown key, or one already placed under an earlier heading.
      if (!option || seen.has(key)) return null;
      seen.add(key);
      members.push(option);
    }
    result.push({ heading: heading.trim(), options: members });
  }

  // Every declaration placed — none left off the screen.
  return seen.size === options.length ? result : null;
}
